import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Lock, Mail } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error("Заполните все поля");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/user/login`, {
        email,
        password
      });

      localStorage.setItem("user_token", response.data.token);
      localStorage.setItem("user_data", JSON.stringify(response.data.user));
      toast.success("Вход выполнен");
      navigate("/cabinet");
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.response?.data?.detail || "Неверный email или пароль");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white/95 backdrop-blur-md border-b border-slate-300 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-4">
          <Button
            onClick={() => navigate("/")}
            variant="ghost"
            size="icon"
            className="hover:bg-blue-50 hover:text-blue-600"
          >
            <ArrowLeft size={24} />
          </Button>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-blue-500/30">
              <span className="text-white font-black text-xl">S</span>
            </div>
            <span className="font-bold text-xl text-slate-800 tracking-tight">
              SKIN<span className="text-blue-600">KOSH</span>
            </span>
          </Link>
        </div>
      </header>

      {/* Login Form */}
      <div className="flex items-center justify-center px-6 py-16">
        <div className="max-w-md w-full bg-white border border-slate-300 p-8 rounded-2xl shadow-xl">
          <div className="w-16 h-16 mx-auto mb-6 bg-blue-100 flex items-center justify-center rounded-xl">
            <Lock className="text-blue-600" size={32} />
          </div>
          
          <h2 className="text-2xl font-bold mb-2 text-center text-slate-800">
            Вход в аккаунт
          </h2>
          <p className="text-slate-500 text-center mb-8">
            Войдите для доступа к личному кабинету
          </p>

          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-slate-600 mb-2 block font-medium">
                <Mail size={16} className="inline mr-2" />
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="h-12 bg-slate-50 border-slate-300 focus:border-blue-500"
                data-testid="login-email"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-600 mb-2 block font-medium">
                <Lock size={16} className="inline mr-2" />
                Пароль
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="h-12 bg-slate-50 border-slate-300 focus:border-blue-500"
                data-testid="login-password"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-14 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-bold text-lg shadow-lg shadow-blue-500/30"
              data-testid="login-submit"
            >
              {loading ? "Загрузка..." : "Войти"}
            </Button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-slate-500">
              Нет аккаунта?{" "}
              <Link to="/register" className="text-blue-600 hover:text-blue-700 font-semibold">
                Зарегистрироваться
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
